import { ActionAutoTriggerType, IntegrationType } from '@doss/prisma/types'
import {
  AmazonSellerSellingRegion,
  amazonSellerIntegrationMetadataSchema,
  aramexIntegrationMetadataSchema,
  billIntegrationMetadataSchema,
  httpEndpointIntegrationMetadataSchema,
  crstlIntegrationMetadataSchema,
  d2IntegrationMetadataSchema,
  cropsterIntegrationMetadataSchema,
  deposcoIntegrationMetadataSchema,
  dossBankingIntegrationMetadataSchema,
  dossBillPayIntegrationMetadataSchema,
  echoShipIntegrationMetadataSchema,
  extensivIntegrationMetadataSchema,
  faireIntegrationMetadataSchema,
  fortySeasIntegrationMetadataSchema,
  ftpIntegrationMetadataSchema,
  gmailIntegrationMetadataSchema,
  GofreightGateway,
  gofreightIntegrationMetadataSchema,
  googleCloudStorageIntegrationMetadataSchema,
  hubSpotIntegrationMetadataSchema,
  ingramMicroIntegrationMetadataSchema,
  locadIntegrationMetadataSchema,
  mintsoftIntegrationMetadataSchema,
  navisphereIntegrationMetadataSchema,
  netsuiteIntegrationMetadataSchema,
  ntgWmsIntegrationMetadataSchema,
  outlookIntegrationMetadataSchema,
  postgresIntegrationMetadataSchema,
  proPacIntegrationMetadataSchema,
  quickbooksIntegrationMetadataSchema,
  routableIntegrationMetadataSchema,
  salsifyIntegrationMetadataSchema,
  shipHeroIntegrationMetadataSchema,
  shipStationIntegrationMetadataSchema,
  shopifyIntegrationMetadataSchema,
  stordIntegrationMetadataSchema,
  stripeIntegrationMetadataSchema,
  tdSynnexIntegrationMetadataSchema,
  thinkLogisticsIntegrationMetadataSchema,
  tikTokShopIntegrationMetadataSchema,
  veracoreIntegrationMetadataSchema,
  wooCommerceIntegrationMetadataSchema,
} from './types/integration-metadata.js'

/**
 * Returns whether the integration type is Organization level or not.
 */
export function isOrganizationLevelIntegration(integrationType: IntegrationType): boolean {
  switch (integrationType) {
    case IntegrationType.GMAIL:
    case IntegrationType.OUTLOOK:
      return false
    case IntegrationType.A2B_FULFILLMENT:
    case IntegrationType.AIRTABLE:
    case IntegrationType.AMAZON_SELLER:
    case IntegrationType.ARAMEX:
    case IntegrationType.BEPRODUCT:
    case IntegrationType.BILL:
    case IntegrationType.BREX:
    case IntegrationType.BUHO:
    case IntegrationType.BRND_SUPPLIER:
    case IntegrationType.BRND_WHOLESALE:
    case IntegrationType.BYRD:
    case IntegrationType.CAMPFIRE:
    case IntegrationType.ORDERFUL:
    case IntegrationType.CROPSTER:
    case IntegrationType.CRSTL:
    case IntegrationType.D2:
    case IntegrationType.DEPOSCO:
    case IntegrationType.DOSS_BANKING:
    case IntegrationType.DOSS_BILL_PAY:
    case IntegrationType.DOSS_EXCHANGE_RATES:
    case IntegrationType.EASYPOST:
    case IntegrationType.ECHO_SHIP:
    case IntegrationType.EXTENSIV:
    case IntegrationType.EXTRON:
    case IntegrationType.FAIRE:
    case IntegrationType.FENO:
    case IntegrationType.FIRSTBASE:
    case IntegrationType.FLEXPORT:
    case IntegrationType.FORTY_SEAS:
    case IntegrationType.FOSDICK:
    case IntegrationType.FTP:
    case IntegrationType.G2MINT:
    case IntegrationType.GNOSIS:
    case IntegrationType.GOFREIGHT:
    case IntegrationType.GOOGLE_CLOUD_STORAGE:
    case IntegrationType.HUBSPOT:
    case IntegrationType.INGRAM_MICRO:
    case IntegrationType.JAY_GROUP:
    case IntegrationType.JAZZ:
    case IntegrationType.JIGSAW:
    case IntegrationType.LAZADA:
    case IntegrationType.MAINFREIGHT:
    case IntegrationType.TQL_FREIGHT:
    case IntegrationType.LOGIWA:
    case IntegrationType.LOOP_RETURNS:
    case IntegrationType.MARKETTIME:
    case IntegrationType.MAYA:
    case IntegrationType.MEDUSA:
    case IntegrationType.MINTSOFT:
    case IntegrationType.MISFITS_MARKET:
    case IntegrationType.NAVISPHERE:
    case IntegrationType.NETSUITE:
    case IntegrationType.NOK_RECOMMERCE:
    case IntegrationType.ONE_WORLD_DIRECT:
    case IntegrationType.POSTGRES:
    case IntegrationType.PRO_PAC:
    case IntegrationType.QUICKBOOKS:
    case IntegrationType.RAMP:
    case IntegrationType.RAPID_PACK:
    case IntegrationType.RILLET:
    case IntegrationType.NTG_WMS:
    case IntegrationType.ROUTABLE:
    case IntegrationType.SALSIFY:
    case IntegrationType.SHIPBOB:
    case IntegrationType.SHIPMONK:
    case IntegrationType.SHIPHERO:
    case IntegrationType.SHIPSIDEKICK:
    case IntegrationType.SHIPSTATION:
    case IntegrationType.SNAPFULFIL:
    case IntegrationType.SHIPWIRE:
    case IntegrationType.SHOPEE:
    case IntegrationType.SHOPIFY:
    case IntegrationType.SILQ:
    case IntegrationType.SPS_COMMERCE:
    case IntegrationType.STORD:
    case IntegrationType.STRIPE:
    case IntegrationType.TD_SYNNEX:
    case IntegrationType.TD_SYNNEX_XML:
    case IntegrationType.LOCAD:
    case IntegrationType.THINK_LOGISTICS:
    case IntegrationType.TIKTOK_SHOP:
    case IntegrationType.TOAST:
    case IntegrationType.UNIS:
    case IntegrationType.USA_SUPPLY:
    case IntegrationType.VERACORE:
    case IntegrationType.CARTONCLOUD:
    case IntegrationType.WALMART:
    case IntegrationType.WAREHANCE:
    case IntegrationType.WESTERN_POST:
    case IntegrationType.WOO_COMMERCE:
    case IntegrationType.XERO:
    case IntegrationType.HTTP_ENDPOINT:
      return true
  }
}

/**
 * Allow multiple connections for these integration types.
 */
export const MULTI_INSTANCE_INTEGRATIONS: IntegrationType[] = [
  IntegrationType.AMAZON_SELLER,
  IntegrationType.ARAMEX,
  IntegrationType.CROPSTER,
  IntegrationType.DEPOSCO,
  IntegrationType.DOSS_BANKING,
  IntegrationType.EXTENSIV,
  IntegrationType.FAIRE,
  IntegrationType.GMAIL,
  IntegrationType.GOFREIGHT,
  IntegrationType.GOOGLE_CLOUD_STORAGE,
  IntegrationType.LAZADA,
  IntegrationType.LOCAD,
  IntegrationType.NAVISPHERE,
  IntegrationType.OUTLOOK,
  IntegrationType.PRO_PAC,
  IntegrationType.QUICKBOOKS,
  IntegrationType.SHIPHERO,
  IntegrationType.SHIPSTATION,
  IntegrationType.SHOPIFY,
  IntegrationType.STORD,
  IntegrationType.STRIPE,
  IntegrationType.TIKTOK_SHOP,
  IntegrationType.WESTERN_POST,
  IntegrationType.XERO,
]

/**
 * Integration types that allow picking their integration via a mapping instead of a fixed
 * dropdown value. Controls whether the "Mapping" mode shows up in the action step editor.
 */
export const INTEGRATION_TYPES_SUPPORTING_MAPPING: IntegrationType[] = [
  IntegrationType.AMAZON_SELLER,
  IntegrationType.CROPSTER,
  IntegrationType.PRO_PAC,
  IntegrationType.DEPOSCO,
  IntegrationType.QUICKBOOKS,
  IntegrationType.SHOPIFY,
  IntegrationType.TIKTOK_SHOP,
]

/**
 * The single `metadata` field each `INTEGRATION_TYPES_SUPPORTING_MAPPING` type is identified by,
 * with the shape that field holds.
 *
 * `field` lets `findIntegrationIdByMetadataValue` target that one field directly instead of matching
 * a mapped value against every key in `metadata`, which could accidentally match an unrelated field
 * that happens to share the same value. `kind` is what lets a generated surface give the field a
 * real type instead of degrading it to arbitrary JSON.
 */
export const INTEGRATION_MAPPING_IDENTIFIER_FIELD: Partial<
  Record<IntegrationType, { readonly field: string; readonly kind: 'string' | 'stringArray' }>
> = {
  [IntegrationType.AMAZON_SELLER]: { field: 'sellingPartnerId', kind: 'string' },
  [IntegrationType.CROPSTER]: { field: 'clientId', kind: 'string' },
  [IntegrationType.DEPOSCO]: { field: 'tenantCode', kind: 'string' },
  [IntegrationType.PRO_PAC]: { field: 'customerId', kind: 'string' },
  [IntegrationType.QUICKBOOKS]: { field: 'realmId', kind: 'string' },
  [IntegrationType.SHOPIFY]: { field: 'shopUrl', kind: 'string' },
  [IntegrationType.TIKTOK_SHOP]: { field: 'shopIds', kind: 'stringArray' },
}

/**
 * Maps webhook auto trigger types to their corresponding integration types.
 */
export const WEBHOOK_AUTO_TRIGGER_TO_INTEGRATION_TYPE: Partial<
  Record<ActionAutoTriggerType, IntegrationType>
> = {
  [ActionAutoTriggerType.AMAZON_SELLER_WEBHOOK]: IntegrationType.AMAZON_SELLER,
  [ActionAutoTriggerType.DOSS_BANKING_WEBHOOK]: IntegrationType.DOSS_BANKING,
  [ActionAutoTriggerType.EXTENSIV_WEBHOOK]: IntegrationType.EXTENSIV,
  [ActionAutoTriggerType.GMAIL_WEBHOOK]: IntegrationType.GMAIL,
  [ActionAutoTriggerType.GOFREIGHT_WEBHOOK]: IntegrationType.GOFREIGHT,
  [ActionAutoTriggerType.LOCAD_WEBHOOK]: IntegrationType.LOCAD,
  [ActionAutoTriggerType.NAVISPHERE_WEBHOOK]: IntegrationType.NAVISPHERE,
  [ActionAutoTriggerType.OUTLOOK_WEBHOOK]: IntegrationType.OUTLOOK,
  [ActionAutoTriggerType.QUICKBOOKS_WEBHOOK]: IntegrationType.QUICKBOOKS,
  [ActionAutoTriggerType.SHIPHERO_WEBHOOK]: IntegrationType.SHIPHERO,
  [ActionAutoTriggerType.SHOPIFY_WEBHOOK]: IntegrationType.SHOPIFY,
  [ActionAutoTriggerType.STORD_WEBHOOK]: IntegrationType.STORD,
  [ActionAutoTriggerType.STRIPE_WEBHOOK]: IntegrationType.STRIPE,
  [ActionAutoTriggerType.TIKTOK_SHOP_WEBHOOK]: IntegrationType.TIKTOK_SHOP,
}

export function isMultiInstanceWebhookAutoTrigger(autoTriggerType: ActionAutoTriggerType): boolean {
  const integrationType = WEBHOOK_AUTO_TRIGGER_TO_INTEGRATION_TYPE[autoTriggerType]
  return integrationType !== undefined && MULTI_INSTANCE_INTEGRATIONS.includes(integrationType)
}

export const AMAZON_SELLER_LOCAL_APP_ID = 'amzn1.sp.solution.391f7755-f97f-4b78-be2b-1c02c448f9df'
export const AMAZON_SELLER_PROD_APP_ID = 'amzn1.sp.solution.7eb4c2b3-8272-40fa-a7a3-23a53207aeb8'

/**
 * Seller Central base URL for the given SP-API selling region.
 * @see https://developer-docs.amazon.com/sp-api/docs/seller-central-urls
 */
export const getAmazonSellerCentralBaseUrl = (sellingRegion: AmazonSellerSellingRegion): string => {
  switch (sellingRegion) {
    case AmazonSellerSellingRegion.NORTH_AMERICA:
      return 'https://sellercentral.amazon.com'
    case AmazonSellerSellingRegion.EUROPE:
      return 'https://sellercentral-europe.amazon.com'
    case AmazonSellerSellingRegion.FAR_EAST:
      return 'https://sellercentral.amazon.co.jp'
    default: {
      const exhaustiveCheck: never = sellingRegion
      return exhaustiveCheck
    }
  }
}

/**
 * Builds the Seller Central OAuth consent URL for Amazon Seller (SP-API) authorization.
 */
export const buildAmazonSellerAuthorizeConsentUrl = (options: {
  sellingRegion: AmazonSellerSellingRegion
  applicationId: string
  state: string
  redirectUri: string
  version?: 'beta'
}): string => {
  const url = new URL(
    '/apps/authorize/consent',
    getAmazonSellerCentralBaseUrl(options.sellingRegion),
  )
  url.searchParams.set('application_id', options.applicationId)
  url.searchParams.set('state', options.state)
  url.searchParams.set('redirect_uri', options.redirectUri)
  if (options.version) {
    url.searchParams.set('version', options.version)
  }
  return url.toString()
}

/**
 * Gets a descriptive identifier for an integration based on its type and metadata.
 * When integrationId is provided, it is appended to disambiguate multiple instances.
 */
export function getIntegrationIdentifier(
  metadata: Record<string, unknown>,
  type: IntegrationType,
  integrationId?: string,
): string {
  const identifier = getIntegrationIdentifierFromMetadata(metadata, type)
  return formatIntegrationIdentifier(identifier, integrationId)
}

function formatIntegrationIdentifier(identifier: string, integrationId?: string): string {
  if (integrationId === undefined || integrationId.length === 0) {
    return identifier
  }
  if (identifier.length === 0) {
    return integrationId
  }
  return `${identifier} (${integrationId})`
}

function getIntegrationIdentifierFromMetadata(
  metadata: Record<string, unknown>,
  type: IntegrationType,
): string {
  if (!metadata) {
    return ''
  }

  switch (type) {
    case IntegrationType.SHOPIFY:
      if (shopifyIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.shopUrl
      }
      break
    case IntegrationType.GMAIL:
      if (gmailIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.emailAddress
      }
      break
    case IntegrationType.OUTLOOK:
      if (outlookIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.emailAddress
      }
      break
    case IntegrationType.WOO_COMMERCE:
      if (wooCommerceIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.storeUrl
      }
      break
    case IntegrationType.QUICKBOOKS:
      if (quickbooksIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.realmId
      }
      break
    case IntegrationType.POSTGRES:
      if (postgresIntegrationMetadataSchema.isValidSync(metadata)) {
        return `${metadata.host}:${metadata.port}/${metadata.database}`
      }
      break
    case IntegrationType.STRIPE:
      if (stripeIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.connectedAccountId
      }
      break
    case IntegrationType.FTP:
      if (ftpIntegrationMetadataSchema.isValidSync(metadata)) {
        return `${metadata.protocol}:${metadata.host}:${metadata.port}`
      }
      break
    case IntegrationType.INGRAM_MICRO:
      if (ingramMicroIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.customerNumber
      }
      break
    case IntegrationType.TD_SYNNEX:
      if (tdSynnexIntegrationMetadataSchema.isValidSync(metadata)) {
        return `Account ${metadata.accountId}`
      }
      break
    case IntegrationType.AMAZON_SELLER:
      if (amazonSellerIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.sellingPartnerId
      }
      break
    case IntegrationType.ARAMEX:
      if (aramexIntegrationMetadataSchema.isValidSync(metadata)) {
        return `${metadata.facility} - ${metadata.storerKey}`
      }
      break
    case IntegrationType.ECHO_SHIP:
      if (echoShipIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.username
      }
      break
    case IntegrationType.D2:
      if (d2IntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.custid
      }
      break
    case IntegrationType.BILL:
      if (billIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.billOrganizationId
      }
      break
    case IntegrationType.EXTENSIV:
      if (extensivIntegrationMetadataSchema.isValidSync(metadata)) {
        return `Customer ${metadata.customerId}`
      }
      break
    case IntegrationType.SHIPHERO:
      if (shipHeroIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.accountId
      }
      break
    case IntegrationType.SHIPSTATION:
      if (shipStationIntegrationMetadataSchema.isValidSync(metadata)) {
        const { partnerAccountId, isStaging } = metadata
        // Partner installs carry an account id — the per-instance identifier. Legacy API-key
        // installs have none, so they fall back to the integration id (empty label here).
        if (
          partnerAccountId !== undefined &&
          partnerAccountId !== null &&
          partnerAccountId !== ''
        ) {
          return isStaging === true ? `${partnerAccountId} (Staging)` : partnerAccountId
        }
      }
      break
    case IntegrationType.HUBSPOT:
      if (hubSpotIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.portalId
      }
      break
    case IntegrationType.MINTSOFT:
      if (mintsoftIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.username
      }
      break
    case IntegrationType.NAVISPHERE:
      if (navisphereIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.customerCode
      }
      break
    case IntegrationType.NETSUITE:
      if (netsuiteIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.accountId
      }
      break
    case IntegrationType.DOSS_BANKING:
      if (dossBankingIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.customerId
      }
      break
    case IntegrationType.DOSS_BILL_PAY:
      if (dossBillPayIntegrationMetadataSchema.isValidSync(metadata)) {
        return `${metadata.organizationId} (${metadata.phoneNumber})`
      }
      break
    case IntegrationType.CRSTL:
      if (crstlIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.crstlOrganizationId
      }
      break
    case IntegrationType.VERACORE:
      if (veracoreIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.systemId
      }
      break
    case IntegrationType.STORD:
      if (stordIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.environment
      }
      break
    case IntegrationType.GOFREIGHT:
      if (gofreightIntegrationMetadataSchema.isValidSync(metadata)) {
        if (metadata.gateway === GofreightGateway.PARTNER) {
          return 'Partner'
        }
        return metadata.useSandbox ? 'Sandbox' : 'Production'
      }
      break
    case IntegrationType.GOOGLE_CLOUD_STORAGE:
      if (!googleCloudStorageIntegrationMetadataSchema.isValidSync(metadata)) {
        break
      }
      if (metadata.serviceAccountEmail) {
        return metadata.serviceAccountEmail
      }
      if (metadata.isExternalProject && metadata.bucketName) {
        return metadata.bucketName
      }
      break
    case IntegrationType.FORTY_SEAS:
      if (fortySeasIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.clientId
      }
      break
    case IntegrationType.ROUTABLE:
      if (routableIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.environment
      }
      break
    case IntegrationType.TIKTOK_SHOP:
      if (tikTokShopIntegrationMetadataSchema.isValidSync(metadata) && metadata.shopIds) {
        return metadata.shopIds.join(', ')
      }
      break
    case IntegrationType.NTG_WMS:
      if (ntgWmsIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.host
      }
      break
    case IntegrationType.LOCAD:
      if (locadIntegrationMetadataSchema.isValidSync(metadata)) {
        return `Brand ${metadata.brandId}`
      }
      break
    case IntegrationType.FAIRE:
      if (faireIntegrationMetadataSchema.isValidSync(metadata)) {
        if (metadata.brandName) {
          return metadata.brandName
        }
        if (metadata.brandId) {
          return metadata.brandId
        }
      }
      break
    case IntegrationType.THINK_LOGISTICS:
      if (thinkLogisticsIntegrationMetadataSchema.isValidSync(metadata)) {
        return `${metadata.custId} (${metadata.whCode})`
      }
      break
    case IntegrationType.SALSIFY:
      if (salsifyIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.orgId
      }
      break
    case IntegrationType.CROPSTER:
      if (cropsterIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.clientId
      }
      break
    case IntegrationType.PRO_PAC:
      if (proPacIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.customerId
      }
      break
    case IntegrationType.DEPOSCO:
      if (deposcoIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.tenantCode
      }
      break
    case IntegrationType.HTTP_ENDPOINT:
      if (httpEndpointIntegrationMetadataSchema.isValidSync(metadata)) {
        return metadata.baseUrl
      }
      break
    // The following integrations do not have any additional metadata to display
    case IntegrationType.A2B_FULFILLMENT:
    case IntegrationType.AIRTABLE:
    case IntegrationType.BEPRODUCT:
    case IntegrationType.BREX:
    case IntegrationType.BRND_SUPPLIER:
    case IntegrationType.BRND_WHOLESALE:
    case IntegrationType.BYRD:
    case IntegrationType.BUHO:
    case IntegrationType.CAMPFIRE:
    case IntegrationType.ORDERFUL:
    case IntegrationType.DOSS_EXCHANGE_RATES:
    case IntegrationType.EASYPOST:
    case IntegrationType.EXTRON:
    case IntegrationType.FLEXPORT:
    case IntegrationType.FENO:
    case IntegrationType.FIRSTBASE:
    case IntegrationType.FOSDICK:
    case IntegrationType.G2MINT:
    case IntegrationType.GNOSIS:
    case IntegrationType.JAY_GROUP:
    case IntegrationType.JAZZ:
    case IntegrationType.JIGSAW:
    case IntegrationType.LAZADA:
    case IntegrationType.LOGIWA:
    case IntegrationType.LOOP_RETURNS:
    case IntegrationType.MAINFREIGHT:
    case IntegrationType.MAYA:
    case IntegrationType.MARKETTIME:
    case IntegrationType.MEDUSA:
    case IntegrationType.NOK_RECOMMERCE:
    case IntegrationType.MISFITS_MARKET:
    case IntegrationType.ONE_WORLD_DIRECT:
    case IntegrationType.RAMP:
    case IntegrationType.RAPID_PACK:
    case IntegrationType.RILLET:
    case IntegrationType.SHIPBOB:
    case IntegrationType.SHIPMONK:
    case IntegrationType.SHIPSIDEKICK:
    case IntegrationType.SNAPFULFIL:
    case IntegrationType.SHIPWIRE:
    case IntegrationType.SHOPEE:
    case IntegrationType.SILQ:
    case IntegrationType.SPS_COMMERCE:
    case IntegrationType.TD_SYNNEX_XML:
    case IntegrationType.TOAST:
    case IntegrationType.TQL_FREIGHT:
    case IntegrationType.USA_SUPPLY:
    case IntegrationType.CARTONCLOUD:
    case IntegrationType.WALMART:
    case IntegrationType.WAREHANCE:
    case IntegrationType.WESTERN_POST:
    case IntegrationType.XERO:
    case IntegrationType.UNIS:
      return ''
  }

  return ''
}
